import React, { useMemo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { TopBox } from "pages/AdminDashboard/AdminDashboardStyled";
import { isCarInUse } from "_helperFunctions/isCarInUse";
import { GiGolfFlag } from "react-icons/gi";
import HoleGapFlag from "assets/images/holeGapFlag.jpg";
import { useHookstate } from "@hookstate/core";
import { UPDATED_CAR_DATA_STATE } from "globalStore/globalStore";

export const GapManagement = () => {
  const { t } = useTranslation();

  const { siteSettingsData } = useSelector(
    (state) => state?.commonReducers
  );
  const {updatedCarData} = useHookstate(UPDATED_CAR_DATA_STATE);
  const [getCurrentCarsData, setGetCurrentCarsData] = useState([]);
  const [carsInUseHoles, setCarsInUseHoles] = useState([]);
  const [courseRecommendations, setCourseRecommendations] = useState([]);

  // Parse updated car data from global store
  useEffect(() => {
    const rawData = updatedCarData.get();
    // Check if rawData is a valid non-empty string before parsing
    if (rawData && typeof rawData === 'string' && rawData.trim() !== '') {
      setGetCurrentCarsData(JSON.parse(rawData));
    }
  }, [updatedCarData.get()]);

  // Get cars in use holes
  useEffect(() => {
    if (!getCurrentCarsData?.length) {
      setCarsInUseHoles([]);
      return;
    }

    const filteredCars = getCurrentCarsData
      .filter(car => isCarInUse(car?.Car, false, false, false))
      .map(car => ({
        hole: car?.Car?.Hole,
        course: car?.Car?.Course
      }))
      .filter(car => car.hole && car.course !== undefined);

    setCarsInUseHoles(filteredCars);
  }, [getCurrentCarsData]);

  // Analyze courses and generate gap management recommendations
  // Priority 1: If holes 1-2 are both available, recommend hole 1 (start boundary)
  // Priority 2: If last two holes are both available, recommend the last hole (end boundary)  
  // Priority 3: Find holes with both adjacent neighbors available, recommend the one closest to course boundaries
  // This ensures optimal cart spacing with strategic positioning near course start/end points
  useEffect(() => {
    if (!siteSettingsData?.Courses) {
      setCourseRecommendations([]);
      return;
    }

    // Group cars by course
    const courseGroups = carsInUseHoles.reduce((groups, car) => {
      if (!groups[car.course]) groups[car.course] = [];
      groups[car.course].push(car.hole);
      return groups;
    }, {});

    // Analyze each course
    const recommendations = siteSettingsData.Courses.map((courseConfig) => {
      if (!courseConfig) return null;
      const occupiedHoles = courseGroups[courseConfig?.Number]
        ? [...new Set(courseGroups[courseConfig?.Number])].sort((a, b) => a - b)
        : [];
      const allHoles = Array.from({ length: courseConfig?.HoleCount }, (_, i) => i + 1);
      const missingHoles = allHoles.filter(hole => !occupiedHoles.includes(hole));
      let recommendedHole = null;
      const firstHole = 1;
      if (missingHoles.length > 0) {
        // Check first record - if next hole is available, use it
        if (missingHoles.includes(firstHole) && missingHoles.includes(firstHole + 1)) {
          recommendedHole = firstHole; // Pick the second hole of the pair
        }
        // Check last record - if previous hole is available, use it  
        else {
          const lastHole = missingHoles[missingHoles.length - 1];
          if (missingHoles.includes(courseConfig?.HoleCount) && missingHoles.includes(courseConfig?.HoleCount - 1)) {
            recommendedHole = lastHole; // Pick the first hole of the pair
          }
          // Only iterate through middle if boundary cases don't work
          else {
            let bestHole = null;
            let bestDistance = Infinity;
            // Check each missing hole to see if it has both previous and next holes available
            for (let hole of missingHoles) {
              const prevHole = hole - 1;
              const nextHole = hole + 1;
              // Check if both previous and next holes are available
              if (missingHoles.includes(prevHole) && missingHoles.includes(nextHole)) {
                // Calculate distance to boundaries
                const distanceTo1 = Math.abs(hole - 1);
                const distanceToLast = Math.abs(hole - courseConfig.HoleCount);
                const minDistance = Math.min(distanceTo1, distanceToLast);

                // Pick the hole closest to boundaries
                if (minDistance < bestDistance) {
                  bestDistance = minDistance;
                  bestHole = hole;
                }
              }
            }
            if (bestHole) {
              recommendedHole = bestHole;
            }
          }
        }
      }
      return {
        courseName: courseConfig.Name,
        recommendedHole: recommendedHole
      };
    });

    setCourseRecommendations(recommendations);
  }, [carsInUseHoles, siteSettingsData]);
  return (
    <>
      <TopBox noAfter style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <div className="header">
          <GiGolfFlag style={{ fontSize: "24px", width: "24px", color: "#666666" }} />
          <p>{t("Gap_Management")}</p>
        </div>
        <div
          className="box-body"
          style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            width: "100%", height: "100%", gap: "20px", boxSizing: "border-box", paddingTop: '10px'
          }}
        >
          <div style={{ width: "100%", maxWidth: "200px" }}>
            <img
              src={HoleGapFlag}
              alt="Golf Flag"
              style={{ width: "100%", height: "auto", display: "block", margin: "0 auto" }} />
          </div>

          <div>
            {courseRecommendations.length > 0 ? (
              courseRecommendations.map((course, index) => (
                <div key={index} style={{ marginBottom: "10px" }}>
                  <Typography>
                    {course.courseName} - {course.recommendedHole ? t("Hole") + `${course.recommendedHole}` : t("No_gap")}
                  </Typography>
                </div>
              ))
            ) : (
              <></>
            )}
          </div>
        </div>
      </TopBox>
    </>
  );
};